import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  DespachoDoThunk,
  DesfechoDoAvanco,
  AvancoDoRelogio,
  ConfigComSimulacao,
} from '../../types';
import { avancoFalhouDepoisDeAndar } from '../../utils/avancoDoRelogio';
import { intencaoReaproveitavel } from '../../utils/intencaoDeComando';
import { reagirARecusaDoComando, buscarSimulacaoAtual } from '../ciclo/actions';
import { intencaoRegistrada, progressoDoAvancoRecebido } from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { servicosDaSimulacao } from '../servicosDaSimulacao';

/**
 * Teto de continuações por clique. O core processa até 20 vencimentos ou 120 s por chamada; um
 * flow que se reagenda dentro do intervalo não pode prender a tela num laço. O que sobra fica em
 * `pendentesDoAvanco`, e o "Tentar novamente" continua de onde parou.
 */
const MAX_CONTINUACOES_DO_AVANCO = 10;

/**
 * Até MAX_CONTINUACOES_DO_AVANCO chamadas a POST /vencimentos, cada uma com chave própria. O slice
 * descarta a chave de toda chamada confirmada ou recusada; só a de desfecho desconhecido fica, e
 * o "Tentar novamente" a reenvia igual. Simulação reiniciada no meio para o laço: a intenção dela
 * cairia por cima das da nova, e a tela já não mostra o que ela processa.
 */
const continuarVencimentos = async (
  simulacaoId: string,
  pendentesIniciais: number,
  dispatch: DespachoDoThunk,
  getState: () => RootState,
): Promise<number> => {
  let pendentes = pendentesIniciais;
  for (let i = 0; i < MAX_CONTINUACOES_DO_AVANCO && pendentes > 0; i += 1) {
    const { intencoes, simulacao } = estadoDaFeature(getState());
    if (simulacao?.id !== simulacaoId) break;
    const intencao = intencaoReaproveitavel(intencoes, 'processarVencimentos', { simulacaoId });
    dispatch(intencaoRegistrada({ tipo: 'processarVencimentos', intencao }));
    const resultado = await servicosDaSimulacao(getState()).processarVencimentos(
      simulacaoId,
      intencao.chave,
    );
    dispatch(progressoDoAvancoRecebido({ ...resultado, simulacaoId }));
    pendentes = resultado.pendentes;
  }
  return pendentes;
};

const podeAvancar = (state: RootState) => {
  const { simulacao, avancando } = estadoDaFeature(state);
  return !avancando && simulacao?.status === 'ATIVA';
};

/**
 * Clicar de novo com a mesma quantidade e unidade depois de um desfecho desconhecido é a mesma
 * intenção: a chave volta, e o core devolve o avanço já feito em vez de somar outro (CA-17). O
 * slice só guarda a chave do avanço enquanto o desfecho dele é desconhecido.
 */
export const avancarRelogioDaSimulacao = createAsyncThunk<
  DesfechoDoAvanco,
  AvancoDoRelogio,
  ConfigComSimulacao
>(
  'negociacaoSimulacao/avancarRelogio',
  async ({ quantidade, unidade }, { dispatch, getState, rejectWithValue }) => {
    const { simulacao, intencoes } = estadoDaFeature(getState());
    const simulacaoId = simulacao?.id ?? '';
    const avanco = { quantidade, unidade };
    const intencao = intencaoReaproveitavel(intencoes, 'avancarRelogio', avanco);
    dispatch(intencaoRegistrada({ tipo: 'avancarRelogio', intencao }));
    try {
      const primeiro = await servicosDaSimulacao(getState()).avancarRelogio(
        simulacaoId,
        avanco,
        intencao.chave,
      );
      dispatch(progressoDoAvancoRecebido({ ...primeiro, simulacaoId }));
      const pendentes = await continuarVencimentos(
        simulacaoId,
        primeiro.pendentes,
        dispatch,
        getState,
      );
      return { simulacaoId, pendentes };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha('avancarRelogio', erro, falha, { simulacaoId });
      if (estadoDaFeature(getState()).simulacao?.id !== simulacaoId)
        return rejectWithValue(falha, { simulacaoId });
      reagirARecusaDoComando(falha, simulacaoId, dispatch);
      // O relógio já andou: a tela relê o que o core gravou, e o retry vai por /vencimentos.
      if (avancoFalhouDepoisDeAndar(falha)) void dispatch(buscarSimulacaoAtual());
      return rejectWithValue(falha, { simulacaoId });
    }
  },
  { condition: (_, { getState }) => podeAvancar(getState()) },
);

/**
 * "Tentar novamente" quando sobrou vencimento: depois das 10 continuações automáticas ou de uma
 * continuação que falhou. O que já foi processado não volta (CAS no core).
 */
export const continuarAvancoDoRelogio = createAsyncThunk<
  DesfechoDoAvanco,
  void,
  ConfigComSimulacao
>(
  'negociacaoSimulacao/continuarAvanco',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { simulacao, pendentesDoAvanco } = estadoDaFeature(getState());
    const simulacaoId = simulacao?.id ?? '';
    try {
      const pendentes = await continuarVencimentos(
        simulacaoId,
        pendentesDoAvanco,
        dispatch,
        getState,
      );
      return { simulacaoId, pendentes };
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha('processarVencimentos', erro, falha, { simulacaoId });
      if (estadoDaFeature(getState()).simulacao?.id !== simulacaoId)
        return rejectWithValue(falha, { simulacaoId });
      reagirARecusaDoComando(falha, simulacaoId, dispatch);
      return rejectWithValue(falha, { simulacaoId });
    }
  },
  {
    condition: (_, { getState }) =>
      podeAvancar(getState()) && estadoDaFeature(getState()).pendentesDoAvanco > 0,
  },
);
